const READ_RETRY_DELAYS_MS = [1_000, 3_000, 10_000] as const;

/** 인증·권한·계약 오류는 보존하고 일시적인 조회 실패만 재시도합니다. */
export function isRetryableReadError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const failure = error as { code?: unknown; name?: unknown; status?: unknown };
  if (failure.code === 'INVALID_RESPONSE' || failure.name === 'AbortError') return false;
  return failure.code === 'NETWORK_ERROR' || failure.name === 'TimeoutError' || (typeof failure.status === 'number' && [0, 408, 429, 500, 502, 503, 504].includes(failure.status));
}

/** 재시도 대기 중 취소를 즉시 반영하고 네트워크 복귀 때는 대기를 앞당깁니다. */
function waitForReadRetry(delayMs: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      clearTimeout(timer);
      signal.removeEventListener('abort', cancel);
      if (typeof window !== 'undefined') window.removeEventListener('online', resume);
    };
    const resume = () => {
      cleanup();
      resolve();
    };
    const cancel = () => {
      cleanup();
      reject(signal.reason);
    };
    const timer = setTimeout(resume, delayMs);
    signal.addEventListener('abort', cancel, { once: true });
    if (typeof window !== 'undefined') window.addEventListener('online', resume, { once: true });
    if (signal.aborted) cancel();
  });
}

/** 부작용 없는 조회에만 사용하며 최초 요청 이후 최대 세 번 재시도합니다. */
export async function retryRead<T>(read: () => Promise<T>, signal: AbortSignal): Promise<T> {
  for (let attempt = 0; ; attempt += 1) {
    signal.throwIfAborted();
    try {
      const result = await read();
      signal.throwIfAborted();
      return result;
    } catch (error) {
      if (signal.aborted || !isRetryableReadError(error) || attempt >= READ_RETRY_DELAYS_MS.length) throw error;
      await waitForReadRetry(READ_RETRY_DELAYS_MS[attempt], signal);
    }
  }
}
